// Container-encoded recordings (WebM/Ogg/MP4) cannot be concatenated by simply
// gluing the bytes together: each MediaRecorder session produces a complete
// file with its own header, and stitching two of them yields a corrupt stream.
//
// To join recordings from separate sessions (e.g. a recovered take plus its
// continuation) we decode each blob to raw PCM via the Web Audio API,
// concatenate the samples, and re-encode a single valid WAV file.

const WAV_MIME_TYPE = 'audio/wav';
const PCM_BYTES_PER_SAMPLE = 2;
const PCM_FORMAT_TAG = 1;
const WAV_HEADER_BYTES = 44;

export interface ConcatenateRecordingBlobsOptions {
  audioContextFactory?: () => BaseAudioContext;
}

const decodeAudioData = (
  audioContext: BaseAudioContext,
  arrayBuffer: ArrayBuffer
): Promise<AudioBuffer> => new Promise((resolve, reject) => {
  const maybePromise = audioContext.decodeAudioData(arrayBuffer, resolve, reject);
  if (maybePromise && typeof (maybePromise as Promise<AudioBuffer>).then === 'function') {
    (maybePromise as Promise<AudioBuffer>).then(resolve, reject);
  }
});

const writeAsciiString = (view: DataView, offset: number, value: string): void => {
  for (let index = 0; index < value.length; index++) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
};

const encodeWavFile = (samples: Float32Array, numChannels: number, sampleRate: number): ArrayBuffer => {
  const blockAlign = numChannels * PCM_BYTES_PER_SAMPLE;
  const dataSize = samples.length * PCM_BYTES_PER_SAMPLE;
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataSize);
  const view = new DataView(buffer);

  writeAsciiString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAsciiString(view, 8, 'WAVE');
  writeAsciiString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, PCM_FORMAT_TAG, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, PCM_BYTES_PER_SAMPLE * 8, true);
  writeAsciiString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = WAV_HEADER_BYTES;
  for (let index = 0; index < samples.length; index++) {
    const clamped = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
    offset += PCM_BYTES_PER_SAMPLE;
  }

  return buffer;
};

// Frames only mean the same amount of time if every buffer shares a sample
// rate. A recovered take re-encoded as WAV and a fresh Opus session routinely
// decode at different rates, and writing one rate into the header while
// concatenating frames from both stretches the result by exactly that ratio.
const resampleToRate = (buffer: AudioBuffer, targetRate: number): Float32Array[] => {
  const channels: Float32Array[] = [];
  const ratio = buffer.sampleRate / targetRate;
  const targetLength = Math.round(buffer.length / ratio);

  for (let channel = 0; channel < buffer.numberOfChannels; channel++) {
    const source = buffer.getChannelData(channel);

    if (ratio === 1) {
      channels.push(source);
      continue;
    }

    const resampled = new Float32Array(targetLength);
    for (let frame = 0; frame < targetLength; frame++) {
      const position = frame * ratio;
      const left = Math.floor(position);
      const right = Math.min(left + 1, source.length - 1);
      const weight = position - left;
      resampled[frame] = source[left] * (1 - weight) + source[right] * weight;
    }
    channels.push(resampled);
  }

  return channels;
};

// Joining segments means re-encoding, and this encoder writes uncompressed
// PCM. At the microphone's native 48kHz that is ~95 KB per second, so a
// resumed conversation uploaded roughly twenty times larger than an
// uninterrupted one. Speech transcription resamples to 16kHz mono anyway, so
// anything above that is bytes spent on detail no consumer of this audio uses.
const SPEECH_SAMPLE_RATE = 16000;

const downmixToMono = (channels: Float32Array[]): Float32Array => {
  if (channels.length === 1) {
    return channels[0];
  }

  const frames = channels[0].length;
  const mono = new Float32Array(frames);
  for (let frame = 0; frame < frames; frame++) {
    let sum = 0;
    for (const channel of channels) {
      sum += channel[frame];
    }
    mono[frame] = sum / channels.length;
  }

  return mono;
};

const encodeAudioBuffersToWav = (buffers: AudioBuffer[]): Blob => {
  // Never upsample: a segment already below the speech rate gains nothing.
  const highestRate = buffers.reduce((max, buffer) => Math.max(max, buffer.sampleRate), 0);
  const sampleRate = Math.min(SPEECH_SAMPLE_RATE, highestRate);

  const monoSegments = buffers.map(
    (buffer) => downmixToMono(resampleToRate(buffer, sampleRate))
  );
  const totalFrames = monoSegments.reduce((sum, segment) => sum + segment.length, 0);
  const joined = new Float32Array(totalFrames);

  let frameOffset = 0;
  for (const segment of monoSegments) {
    joined.set(segment, frameOffset);
    frameOffset += segment.length;
  }

  return new Blob([encodeWavFile(joined, 1, sampleRate)], { type: WAV_MIME_TYPE });
};

const createDefaultAudioContext = (): BaseAudioContext => {
  const AudioContextConstructor = window.AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!AudioContextConstructor) {
    throw new Error('AudioContext is not available for recording concatenation');
  }

  return new AudioContextConstructor();
};

export const concatenateRecordingBlobs = async (
  blobs: Blob[],
  options: ConcatenateRecordingBlobsOptions = {}
): Promise<Blob> => {
  const usableBlobs = blobs.filter((blob) => blob.size > 0);

  if (usableBlobs.length === 0) {
    return new Blob([], { type: blobs[0]?.type ?? WAV_MIME_TYPE });
  }

  // A single session needs no re-encoding: keep the original, lossless blob.
  if (usableBlobs.length === 1) {
    return usableBlobs[0];
  }

  const audioContext = (options.audioContextFactory ?? createDefaultAudioContext)();
  try {
    const audioBuffers: AudioBuffer[] = [];
    for (const blob of usableBlobs) {
      audioBuffers.push(await decodeAudioData(audioContext, await blob.arrayBuffer()));
    }

    return encodeAudioBuffersToWav(audioBuffers);
  } finally {
    await (audioContext as { close?: () => Promise<void> }).close?.();
  }
};
